<!-- AuthComponent.vue -->
<template>
  <div class="auth-wrapper">
    <div class="container d-flex justify-content-center position-relative content-layer">
      <div class="col-md-6">
        <div class="card">
        <div class="card-header">
          <h5 class="mb-0">
            <!-- Cambia el título en función de la vista -->
            {{ isLoginOpen ? $t('auth.login') : $t('auth.register') }}
          </h5>
        </div>
        <div class="card-body">
          <!-- FORMULARIO DE LOGIN -->
          <div v-if="isLoginOpen">
            <form @submit.prevent="login">
              <div class="mb-3">
                <label for="username" class="form-label">{{ $t('auth.form.email') }}:</label>
                <input
                    type="text"
                    class="form-control"
                    id="username"
                    v-model="formData.email"
                    required
                    :class="{ 'is-invalid': loginError && loginError.errors && loginError.errors.email }"
                />
                <div v-if="loginError && loginError.errors && loginError.errors.email" class="invalid-feedback">
                  {{ loginError.errors.email[0] }}
                </div>
              </div>
              <div class="mb-3">
                <label for="password" class="form-label">{{ $t('auth.form.password') }}:</label>
                <div class="input-group mb-3">
                  <input
                      type="password"
                      class="form-control"
                      id="password"
                      v-model="formData.password"
                      required
                      :class="{ 'is-invalid': loginError && loginError.errors && loginError.errors.password }"
                  />
                  <button
                      class="btn btn-outline-secondary"
                      type="button"
                      @click="togglePasswordVisibility('password')"
                      :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                  >
                    <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                  </button>
                  <div v-if="loginError && loginError.errors && loginError.errors.password" class="invalid-feedback">
                    {{ loginError.errors.password[0] }}
                  </div>
                </div>
              </div>
              <div class="text-end mb-3" v-if="!isPreviewEnv">
                <button type="button" class="btn btn-link p-0 small text-muted" @click.stop="goToForgot">
                  {{ $t('auth.forgot_password') }}
                </button>
              </div>

              <div class="text-center">
                <button type="submit" class="btn btn-secondary">
                  <span v-if="!loading">{{ $t('auth.form.btn') }}</span>
                  <span v-else>
                    <i class="fa fa-spinner fa-spin"></i> Procesando...
                  </span>
                </button>
              </div>
            </form>

            <!-- Error genérico si existe -->
            <div v-if="loginError && loginError.errors && !loginError.message" class="alert alert-danger mt-3">
              {{ getErrorMessage(loginError.errors.message) }}
            </div>
            <div v-if="isLoggedIn" class="alert alert-success mt-3">
              ¡Has iniciado sesión correctamente!
            </div>

            <!-- Enlace para ir a register -->
            <div class="mt-3 text-center" v-if="!isPreviewEnv">
              <span>{{ $t('auth.no_account') }}</span>
              <button class="btn btn-sm btn-outline-secondary" @click.stop="toggleForm">
                {{ $t('auth.sign_up') }}
              </button>
            </div>
          </div>

          <!-- FORMULARIO DE REGISTRO -->
          <div v-else>
            <form @submit.prevent="register">
              <div class="mb-3">
                <label for="name" class="form-label">{{ $t('auth.form.name') }}:</label>
                <input
                    type="text"
                    class="form-control"
                    id="name"
                    v-model="formRegister.name"
                    required
                    :class="{ 'is-invalid': registerError && registerError.errors && registerError.errors.name }"
                />
                <div v-if="registerError && registerError.errors && registerError.errors.name" class="invalid-feedback">
                  {{ registerError.errors.name[0] }}
                </div>
              </div>
              <div class="mb-3">
                <label for="surname" class="form-label">{{ $t('auth.form.surname') }}:</label>
                <input
                    type="text"
                    class="form-control"
                    id="surname"
                    v-model="formRegister.surname"
                    required
                />
                <div v-if="registerError && registerError.errors && registerError.errors.surname" class="invalid-feedback">
                  {{ registerError.errors.surname[0] }}
                </div>
              </div>
              <div class="mb-3">
                <label for="email" class="form-label">{{ $t('auth.form.email') }}:</label>
                <input
                    type="text"
                    class="form-control"
                    id="email"
                    v-model="formRegister.email"
                    required
                    :class="{ 'is-invalid': registerError && registerError.errors && registerError.errors.email }"
                />
                <div v-if="registerError && registerError.errors && registerError.errors.email" class="invalid-feedback">
                  {{ registerError.errors.email[0] }}
                </div>
              </div>
              <div class="mb-3">
                <label for="password-register" class="form-label">{{ $t('auth.form.password') }}:</label>
                <small class="form-text text-muted d-block">
                  {{ $t('auth.password_requirements.title') }}
                </small>
                <ul class="list-unstyled password-rules mb-3">
                  <li
                      v-for="rule in passwordRequirements"
                      :key="rule.key"
                      :class="['d-flex align-items-center', rule.met ? 'text-success' : 'text-muted']"
                  >
                    <i class="fas" :class="rule.met ? 'fa-check-circle' : 'fa-circle'"></i>
                    <span class="ms-2">
                      <template v-if="rule.key === 'special'">
                        {{ $t('auth.password_requirements.special') }} ({{ allowedSpecialChars }})
                      </template>
                      <template v-else>
                        {{ $t(`auth.password_requirements.${rule.key}`) }}
                      </template>
                    </span>
                  </li>
                </ul>
                <div class="input-group mb-3">
                  <input
                      type="password"
                      class="form-control"
                      id="password-register"
                      v-model="formRegister.password"
                      required
                      :class="{ 'is-invalid': errors.password }"
                  />
                  <button
                      class="btn btn-outline-secondary"
                      type="button"
                      @click="togglePasswordVisibility('password-register')"
                      :aria-label="showPasswordRegister ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                  >
                    <i class="fas" :class="showPasswordRegister ? 'fa-eye-slash' : 'fa-eye'"></i>
                  </button>
                  <div v-if="errors.password" class="invalid-feedback">
                    {{ errors.password }}
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <label for="password-confirm" class="form-label">{{ $t('auth.form.password_confirmation') }}:</label>
                <div class="input-group mb-3">
                  <input
                      type="password"
                      class="form-control"
                      id="password-confirm"
                      v-model="formRegister.password_confirmation"
                      required
                      :class="{ 'is-invalid': errors.password_confirmation }"
                  />
                  <button
                      class="btn btn-outline-secondary"
                      type="button"
                      @click="togglePasswordVisibility('password-confirm')"
                      :aria-label="showPasswordRegisterRepl ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                  >
                    <i class="fas" :class="showPasswordRegisterRepl ? 'fa-eye-slash' : 'fa-eye'"></i>
                  </button>
                  <div v-if="errors.password_confirmation" class="invalid-feedback">
                    {{ errors.password_confirmation }}
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <label for="roles" class="form-label">{{ $t('auth.form.roles') || 'Roles' }}:</label>
                <div
                    id="roles"
                    class="roles-pill-group"
                    :class="{ 'is-invalid': registerError && registerError.errors && (registerError.errors.role_ids || registerError.errors.roles) }"
                >
                  <button
                      v-for="role in rolesOptions"
                      :key="role.id"
                      type="button"
                      class="role-pill"
                      :class="{ 'role-pill--active': isRoleSelected(role.id) }"
                      :aria-pressed="isRoleSelected(role.id)"
                      @click="toggleRoleSelection(role.id)"
                  >
                    {{ role.name }}
                  </button>
                </div>
                <div v-if="registerError && registerError.errors && (registerError.errors.role_ids || registerError.errors.roles)" class="invalid-feedback">
                  {{ (registerError.errors.role_ids && registerError.errors.role_ids[0]) || (registerError.errors.roles && registerError.errors.roles[0]) }}
                </div>
                <small class="form-text text-muted">{{ $t('auth.form.roles_hint') || 'Select one or more roles. If you skip this step, Athlete will be assigned automatically.' }}</small>
              </div>
              <div class="mb-3">
                <label for="captcha" class="form-label">¿{{ captcha.question }}?:</label>
                <div class="input-group mb-3">
                  <input
                      type="text"
                      class="form-control"
                      id="captcha"
                      v-model="formRegister.captchaAnswer"
                      required
                      :class="{ 'is-invalid': registerError && registerError.errors && registerError.errors.captchaAnswer }"
                  />
                  <div v-if="registerError && registerError.errors && registerError.errors.captchaAnswer" class="invalid-feedback">
                    {{ registerError.errors.captchaAnswer }}
                  </div>
                </div>
                <small class="form-text text-muted">{{ $t('auth.captcha_result_hint') }}</small>
              </div>
              <div class="text-center">
                <button type="submit" class="btn btn-secondary">
                  <span v-if="!loading">{{ $t('auth.form.btn_register') }}</span>
                  <span v-else>
                    <i class="fa fa-spinner fa-spin"></i> Procesando...
                  </span>
                </button>
              </div>
            </form>

            <div v-if="registerError && registerError.errors && registerError.errors.message" class="alert alert-danger mt-3">
              {{ getErrorMessage(registerError.errors.message) }}
            </div>

            <!-- Enlace para ir a login -->
            <div class="mt-3 text-center">
              <span>{{ $t('auth.have_account') }}</span>
              <button class="btn btn-sm btn-outline-secondary" @click="toggleForm">
                {{ $t('auth.login') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import router from '@/router/router';

import {mapActions, mapGetters} from "vuex";
import { isPreviewEnv as isPreviewEnvHelper } from "@/services/hostContext";
export default {
  name: "AuthComponent",
  data() {
    return {
      isLoggedIn: false,
      loginError: null,
      registerError: null,
      loading: false, // Maneja el spinner
      // Controla si se muestra el login o register
      isLoginOpen: true,

      // Formulario de Login
      formData: {
        email: "",
        password: "",
      },

      // Formulario de Registro
      formRegister: {
        name: "",
        surname: "",
        email: "",
        password: "",
        password_confirmation: "",
        captchaAnswer: "",
      },

      errors: {
        password: null,
        password_confirmation: null,
      },

      captcha: {
        question: "",
        correctAnswer: 0,
      },

      // Control de visibilidad en contraseñas
      showPassword: false,
      showPasswordRegister: false,
      showPasswordRegisterRepl: false,

      // Roles
      rolesOptions: [],
      selectedRoles: [],

      allowedSpecialChars: '@$!%*?&',

    };
  },
  computed: {
    ...mapGetters('user', ['getRoles']),
    roles() {
      return this.getRoles.filter((role) => role.name.toLowerCase() !== "admin");
    },
    isPreviewEnv() {
      return isPreviewEnvHelper(this.$hostContext && this.$hostContext.env);
    },
    passwordRequirements() {
      const rules = this.getPasswordRulesStatus();
      return [
        { key: 'minLength', met: rules.minLength },
        { key: 'lowercase', met: rules.lowercase },
        { key: 'uppercase', met: rules.uppercase },
        { key: 'number', met: rules.number },
        { key: 'special', met: rules.special },
      ];
    },
  },
  async mounted() {
    this.generateCaptcha();
    await this.fetchRoles();
  },
  methods: {
    ...mapActions('user', ['setLogin','setLogout']),
    showLoading() {
      this.$nextTick(() => {
        if (this.$root.$refs.globalLoading) {
          this.$root.$refs.globalLoading.showLoading();
        }
      });
    },
    hideLoading() {
      this.$nextTick(() => {
        if (this.$root.$refs.globalLoading) {
          this.$root.$refs.globalLoading.hideLoading();
        }
      });
    },
    generateCaptcha() {
      const num1 = Math.floor(Math.random() * 10);
      const num2 = Math.floor(Math.random() * 10);
      this.captcha.question = `${num1} + ${num2}`;
      this.captcha.correctAnswer = num1 + num2;
    },
    getPasswordRulesStatus() {
      const password = this.formRegister.password || "";
      return {
        minLength: password.length >= 6,
        lowercase: /[a-z]/.test(password),
        uppercase: /[A-Z]/.test(password),
        number: /\d/.test(password),
        special: /[@$!%*?&]/.test(password),
      };
    },
    async login() {
      this.loginError = null;
      this.showLoading();
      try {
        const response = await this.$axios.post("/login", {
          email: this.formData.email,
          password: this.formData.password,
        });
        const ok = this.setLogin(response.data);
        if (ok) {
          this.isLoggedIn = true;
          await router.push("/home");
        }
      } catch (error) {
        if (error.response) {
          this.loginError = error.response.data;
        } else {
          this.loginError = { message: "Error de red o respuesta indefinida" };
        }
      } finally {
        this.hideLoading();
      }
    },
    goToForgot() {
      console.log('entro');
      router.push('/forgot-password');
    },
    validatePasswords() {
      // Reiniciar los errores
      this.errors.password = null;
      this.errors.password_confirmation = null;

      const passwordRules = this.getPasswordRulesStatus();
      const isPasswordValid = Object.values(passwordRules).every(Boolean);
      if (!isPasswordValid) {
        this.errors.password =
            "La contraseña debe tener al menos 6 caracteres, una mayúscula, un número y un carácter especial.";
      }

      // Validar si las contraseñas coinciden
      if (this.formRegister.password !== this.formRegister.password_confirmation) {
        this.errors.password_confirmation = "Las contraseñas no coinciden.";
      }

      // Validar si la confirmación está vacía
      if (!this.formRegister.password_confirmation) {
        this.errors.password_confirmation = "La confirmación de contraseña es obligatoria.";
      }
    },
    async register() {
      this.validatePasswords();
      if (this.errors.password || this.errors.password_confirmation) {
        return;
      }
      this.showLoading();
      try {
        const athleteRole = this.rolesOptions.find(role => role.name === 'Athlete');
        const roleIds = Array.isArray(this.selectedRoles) ? [...new Set(this.selectedRoles)] : [];
        if (roleIds.length === 0 && athleteRole) {
          roleIds.push(athleteRole.id);
        }
        const response = await this.$axios.post("/register", {
          name: this.formRegister.name,
          surname: this.formRegister.surname,
          email: this.formRegister.email,
          password: this.formRegister.password,
          password_confirmation: this.formRegister.password_confirmation,
          captchaQuestion: this.captcha.question,
          captchaAnswer: this.formRegister.captchaAnswer,
          role_ids: roleIds,
        });
        const ok = this.setLogin(response.data);
        if (ok) {
          this.isLoggedIn = true;
          await router.push("/home");
        }
      } catch (error) {
        if (error.response) {
          this.registerError = error.response.data;
          if (this.registerError.errors && this.registerError.errors.captchaAnswer) {
            this.generateCaptcha();
          }
        } else {
          this.registerError = { message: "Error de red o respuesta indefinida" };
        }
      } finally {
        this.hideLoading();
      }
    },
    getErrorMessage(errorCode) {
      switch (errorCode) {
        case "error-email":
          return this.$t("auth.errors.invalidEmail");
        case "error-password":
          return this.$t("auth.errors.incorrectPassword");
        case "error-test-access-blocked":
          return this.$te && this.$te("auth.errors.testAccessBlocked")
            ? this.$t("auth.errors.testAccessBlocked")
            : "Access to this test environment is blocked for this user.";
        default:
          return this.$t("auth.errors.unauthorized");
      }
    },
    togglePasswordVisibility(fieldId) {
      const input = document.getElementById(fieldId);
      if (input) {
        input.type = input.type === "password" ? "text" : "password";
      }
    },
    toggleForm() {
      this.isLoginOpen = !this.isLoginOpen;
    },
    isRoleSelected(roleId) {
      return this.selectedRoles.includes(roleId);
    },
    toggleRoleSelection(roleId) {
      if (this.isRoleSelected(roleId)) {
        this.selectedRoles = this.selectedRoles.filter(id => id !== roleId);
        return;
      }
      this.selectedRoles = [...this.selectedRoles, roleId];
    },
    async fetchRoles() {
      try {
        const response = await this.$axios.get("/roles");
        const blockedRoles = ['Admin', 'Supervisor'];
        this.rolesOptions = (response.data.roles || []).filter(role => !blockedRoles.includes(role.name));
      } catch (error) {
        console.error("Error al obtener los roles:", error);
      }
    },
  },
  components: {
  },
};
</script>

<style scoped>
.card-header.active {
  background-color: #6c757d;
  color: #ffffff;
}

.auth-wrapper {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}

.content-layer {
  position: relative;
  z-index: 1;
}

.watermark-overlay {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 200 200'%3E%3Ctext x='0' y='120' fill='rgba(0,0,0,0.06)' font-size='32' transform='rotate(-30 100 100)'%3ETest Area%3C/text%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 240px 240px;
  opacity: 1;
}

.password-rules {
  font-size: 0.875rem;
}

.password-rules .fa-circle,
.password-rules .fa-check-circle {
  font-size: 0.75rem;
}

.roles-pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.role-pill {
  border: 1px solid #ced4da;
  border-radius: 999px;
  background: #ffffff;
  color: #495057;
  padding: 8px 14px;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.role-pill:hover {
  border-color: #6c757d;
  background: #f8f9fa;
}

.role-pill--active {
  background: #6c757d;
  border-color: #6c757d;
  color: #ffffff;
}

@media (max-width: 576px) {
  .roles-pill-group {
    flex-direction: column;
  }

  .role-pill {
    width: 100%;
    text-align: center;
  }
}
</style>
